import React, { useState, useEffect } from "react";

import {
  DataTable,
  TableContainer,
  TableHeader,
  TableRow,
  TableBody,
  TableHead,
  Table,
  TableCell,
} from "carbon-components-react";

import APIClient from "web_modules/APIClient";
import { AppShape } from "@eave/shared/types/api";

import Money from "../../../Money";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

interface AccountAnalysisProps {
  app: AppShape;
  updateCounter: Number;
}

const AccountAnalysis: React.FC<AccountAnalysisProps> = (props: AccountAnalysisProps) => {
  const { app, updateCounter } = props;

  const [details, setDetails] = useState(null);
  const [flattenedRows, setFlattenedRows] = useState([]);

  useEffect(() => {
    APIClient.get(`/uw/${app.id}/escrows_analysis`).then((d: any) => {
      // flatten rows
      const f = [];
      if (d && d.rows && d.rows.length > 0) {
        for (let i = 0; i < d.rows.length; i++) {
          const row = d.rows[i];
          row.disbursed = row.disbursements[0] && row.disbursements[0].amount;
          row.disbursement_name = row.disbursements[0] && row.disbursements[0].name;
          row.disbursement_date = row.disbursements[0] && row.disbursements[0].date;
          f.push(row);
          for (let j = 1; j < row.disbursements.length; j++) {
            const extraRow = {
              disbursed: row.disbursements[j].amount,
              disbursement_date: row.disbursements[j].date,
              disbursement_name: row.disbursements[j].name,
              balance: row.balance,
            };
            f.push(extraRow);
          }
        }
      }
      setDetails(d);
      setFlattenedRows(f);
    });
  }, [updateCounter]);

  return (
    <DataTable
      rows={(flattenedRows || []).map((r, i) => {
        return {
          ...r,
          balance: <Money value={r.balance} />,
          disbursed: <Money value={r.disbursed || 0} />,
          id: `${r.month_number}_${i}`,
        };
      })}
      headers={makeHeaderData({
        month_number: "Month",
        month: "Mo./Year",
        disbursed: "Disbursed",
        disbursement_date: "Date Paid",
        disbursement_name: "Disbursement",
        balance: "Balance",
      })}
      render={({ rows, headers, getRowProps, getHeaderProps }) => (
        <TableContainer title="Initial Escrow Account Analysis" style={{ maxWidth: "80rem" }}>
          {details && details.aggregate_adjustment && (
            <Table size="compact">
              <TableHead>
                <TableRow>
                  <TableHeader>Aggregate Adjustment</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{details && <Money value={details.aggregate_adjustment} />}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          <Table size="compact">
            <TableHead>
              <TableRow>
                <TableHeader>Starting Balance</TableHeader>
                <TableHeader>Monthly Inflow</TableHeader>
                <TableHeader>Low Balance</TableHeader>
                <TableHeader>Cushion</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{details && <Money value={details.starting_balance} />}</TableCell>
                <TableCell>{details && <Money value={details.monthly_inflow} />}</TableCell>
                <TableCell>{details && <Money value={details.low_balance} />}</TableCell>
                <TableCell>{details && <Money value={details.cushion} />}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Table size="compact">
            <TableHead>
              <TableRow>
                {headers.map(header => (
                  <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id} {...getRowProps({ row })}>
                  {row.cells.map(cell => (
                    <TableCell key={cell.id}>{cell.value}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    />
  );
};

export default AccountAnalysis;
